/**
 * SpecialistID — Product PDP modular bundle: DATA
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns product/variant JSON loading and all read access to it: metafields,
 * prices, and the currently-selected variant id. Pure read/normalize layer
 * over SPID.state.productData — no DOM mutation, no UI side effects.
 *
 * LOAD ORDER: after custom-product-core.js, before every other
 * custom-product-* file.
 *
 * Global JSON contract kept intact for FPD + other scripts:
 *   window.product (input), window.productData (output),
 *   window.isProductDataLoaded(), window.getCurrentVariantData().
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const debugLog = SPID.debugLog;

  // Load product data from JSON - returns a Promise
  SPID.loadProductData = function () {
    // Return existing promise if already loading
    if (SPID.state.productDataPromise) {
      return SPID.state.productDataPromise;
    }

    // If already loaded, return resolved promise
    if (SPID.state.productData) {
      return Promise.resolve(SPID.state.productData);
    }

    SPID.state.productDataPromise = new Promise((resolve, reject) => {
      // Method 1: Try to get from ProductJson script tag
      const productJsonScript = document.querySelector('[id^="ProductJson-"]');
      if (productJsonScript) {
        try {
          SPID.state.productData = JSON.parse(productJsonScript.textContent);
          window.productData = SPID.state.productData;
          debugLog('Product data loaded from script tag');
          resolve(SPID.state.productData);
          return;
        } catch (e) {
          console.error('Error parsing ProductJson:', e);
        }
      }

      // Method 2: Try to get from window object (many themes expose this)
      if (window.product) {
        SPID.state.productData = window.product;
        window.productData = SPID.state.productData;
        debugLog('Product data loaded from window.product');
        resolve(SPID.state.productData);
        return;
      }

      // Method 3: Try to fetch from API if we have a product handle
      const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/);
      if (productHandle && productHandle[1]) {
        fetch(`/products/${productHandle[1]}.json`)
          .then(response => response.json())
          .then(data => {
            SPID.state.productData = data.product;
            window.productData = SPID.state.productData;
            debugLog('Product data loaded from API');
            resolve(SPID.state.productData);
          })
          .catch(error => {
            console.error('Failed to load product JSON from API:', error);
            reject(error);
          });
      } else {
        // No product data available
        debugLog('No product data source found');
        resolve(null);
      }
    });

    return SPID.state.productDataPromise;
  };

  // Convenience accessor for the loaded product JSON
  SPID.getProductData = function () {
    return SPID.state.productData;
  };

  // Helper function to check if product data is loaded
  window.isProductDataLoaded = function () {
    return !!SPID.state.productData;
  };

  // Helper function to get current variant data
  window.getCurrentVariantData = function () {
    const variantId = SPID.getVariantId();
    return variantId ? SPID.getVariantData(variantId) : null;
  };

  // Get variant data from JSON
  SPID.getVariantData = function (variantId) {
    if (!variantId) return null;
    const productData = SPID.state.productData;
    if (!productData || !productData.variants) return null;

    // Ensure variantId is a string for comparison
    const variantIdStr = variantId.toString();

    return productData.variants.find(v => {
      // Handle both numeric and string IDs
      return v.id == variantIdStr || v.id === parseInt(variantIdStr);
    });
  };

  // Get product metafield value with support for both flat and nested structures
  SPID.getProductMetafield = function (namespace, key) {
    const productData = SPID.state.productData;
    if (!productData) return null;

    // Method 1: Check nested structure (productData.metafields.namespace.key)
    if (productData.metafields && typeof productData.metafields === 'object') {
      if (productData.metafields[namespace] && productData.metafields[namespace][key] !== undefined) {
        return productData.metafields[namespace][key];
      }
    }

    // Method 2: Check array structure (Shopify API format)
    if (Array.isArray(productData.metafields)) {
      const metafield = productData.metafields.find(m =>
        m.namespace === namespace && m.key === key
      );
      if (metafield) return metafield.value;
    }

    return null;
  };

  // Get variant metafield value with support for both flat and nested structures
  SPID.getVariantMetafield = function (variantId, namespace, key) {
    const variant = SPID.getVariantData(variantId);
    if (!variant) return null;

    let value = null;

    // Method 1: Check nested structure (variant.metafields.namespace.key)
    if (variant.metafields && typeof variant.metafields === 'object') {
      if (variant.metafields[namespace] && variant.metafields[namespace][key] !== undefined) {
        value = variant.metafields[namespace][key];
      }
    }

    // Method 2: Check array structure (Shopify API format)
    if (value === null && Array.isArray(variant.metafields)) {
      const metafield = variant.metafields.find(m =>
        m.namespace === namespace && m.key === key
      );
      if (metafield) value = metafield.value;
    }

    // Strip $ sign from value if present (for price fields)
    if (value !== null && typeof value === 'string') {
      value = value.replace(/^\$/, '');
    }

    return value;
  };

  // Get collection data from page context (if available)
  SPID.getCollectionMetafield = function (namespace, key) {
    // Try to get from window.collection if theme exposes it
    if (window.collection && window.collection.metafields) {
      if (typeof window.collection.metafields === 'object') {
        if (window.collection.metafields[namespace] && window.collection.metafields[namespace][key] !== undefined) {
          return window.collection.metafields[namespace][key];
        }
      }
      if (Array.isArray(window.collection.metafields)) {
        const metafield = window.collection.metafields.find(m =>
          m.namespace === namespace && m.key === key
        );
        if (metafield) return metafield.value;
      }
    }
    return null;
  };

  // Helper function to get variant price
  SPID.getVariantPrice = function (variantId) {
    if (!variantId) {
      debugLog('getVariantPrice: No variantId provided');
      return 0;
    }

    const variant = SPID.getVariantData(variantId);
    if (!variant) {
      console.error(`getVariantPrice: Variant not found for ID: ${variantId}`);
      return 0;
    }

    if (variant.price === undefined || variant.price === null || variant.price === 0) {
      debugLog(`getVariantPrice: Variant price not available, falling back to product price for variant: ${variantId}`);
      // Fall back to product price (added to ProductJson in main-product.liquid)
      const productData = SPID.state.productData;
      if (productData && productData.price) {
        const productPrice = typeof productData.price === 'string' ? parseFloat(productData.price) : productData.price;
        // Check if price is in dollars or cents (same logic as variant price)
        if (productPrice < 100 && typeof productData.price === 'string') {
          return productPrice;
        }
        return productPrice / 100;
      }
      return 0;
    }

    debugLog('Raw variant.price from JSON:', variant.price, 'for variant:', variantId);

    // Check if price is already in dollars (< 100) or in cents (>= 100)
    // Shopify's product.json API returns prices as strings like "1.58"
    // but ProductJson-* script tags return cents as numbers
    const priceValue = typeof variant.price === 'string' ? parseFloat(variant.price) : variant.price;

    // If price is less than 100 and looks like dollars already, don't divide
    // This handles cases where Shopify returns "1.58" as a string
    if (priceValue < 100 && typeof variant.price === 'string') {
      debugLog('Price appears to be in dollars already:', priceValue);
      return priceValue;
    }

    // Otherwise treat as cents
    const finalPrice = priceValue / 100;
    debugLog('Converted price from cents:', finalPrice);
    return finalPrice;
  };

  // Helper function to get variant compare at price
  SPID.getVariantCompareAtPrice = function (variantId) {
    if (!variantId) {
      debugLog('getVariantCompareAtPrice: No variantId provided');
      return 0;
    }

    const variant = SPID.getVariantData(variantId);
    if (!variant) {
      debugLog(`getVariantCompareAtPrice: Variant not found for ID: ${variantId}`);
      return 0;
    }

    if (!variant.compare_at_price) {
      debugLog(`getVariantCompareAtPrice: No compare_at_price for variant: ${variantId}`);
      return 0;
    }

    debugLog('Raw variant.compare_at_price from JSON:', variant.compare_at_price, 'for variant:', variantId);

    // Check if price is already in dollars or in cents (same logic as getVariantPrice)
    const priceValue = typeof variant.compare_at_price === 'string' ? parseFloat(variant.compare_at_price) : variant.compare_at_price;

    // If price is less than 100 and looks like dollars already, don't divide
    if (priceValue < 100 && typeof variant.compare_at_price === 'string') {
      debugLog('Compare price appears to be in dollars already:', priceValue);
      return priceValue;
    }

    // Otherwise treat as cents
    const finalPrice = priceValue / 100;
    debugLog('Converted compare price from cents:', finalPrice);
    return finalPrice;
  };

  // Resolve the currently-selected variant id from the various PDP option UIs
  SPID.getVariantId = function () {
    var variant_id = document.querySelector('input[name="Color"]:checked')?.dataset.variantid;
    if (variant_id === undefined) {
      variant_id = document.querySelector('input[name="Size"]:checked')?.dataset.variantid;
    }
    if (variant_id === undefined) {
      variant_id = document.querySelector('input[name="Layout"]:checked')?.dataset.variantid;
    }
    if (variant_id === undefined) {
      variant_id = document.querySelector('input[name="Pre-Printed"]:checked')?.dataset.variantid;
    }
    if (variant_id === undefined) {
      const variantCount = document.querySelectorAll('input[name="variant_id"]').length;
      if (variantCount === 1) {
        variant_id = document.querySelector('input[name="variant_id"]')?.value;
      }
    }
    if (variant_id === undefined) {
      variant_id = document.querySelector('input.color-swatch-item:checked')?.dataset.variantid;
    }
    if (variant_id === undefined) {
      variant_id = document.querySelector('input.current_variant_id')?.value;
    }
    return variant_id;
  };

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('data');
})();
